import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { replaceBracketPlaceholders } from '../utilities/replaceBracketPlaceholder';
import { vsprintf } from "sprintf-js";

const ColumnAccount = ({ params, item, handleRowUpdate, colAccountOptions = [], formAction, documentSelectTableData }) => {
  const displayField = item?.inputType?.data?.sDisplayField || '';
  const displayFormat = item?.inputType?.data?.sDisplayFormat || '';
  const valueField = item?.inputType?.data?.sValueField;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (formAction === "EDIT" || documentSelectTableData?.length > 0) {
      return;
    }
    handleRowUpdate({
      fieldName: item?.sColumnID,
      value: item?.inputType?.component?.sDefaultValue,
      rowId: params.row.id,
    });
  }, []);

  const processedOptions = useMemo(() => {
    return (colAccountOptions || []).map(opt => {
      const keys = [...(displayField.matchAll(/{(.*?)}/g) || [])].map(m => m[1]);
      const args = keys.map(k => {
        const v = opt[k];
        return v === undefined || v === null ? "" : String(v);
      });
      let _display = "";
      try {
        _display = vsprintf(displayFormat ?? "%s", args);
      } catch (e) {
        _display = args.join(", ");
      }
      return { ...opt, _display };
    });
  }, [colAccountOptions, displayField, displayFormat]);

  const selectedValue = useMemo(() => {
    const rowVal = params?.row?.[item?.sColumnID];
    if (rowVal == null) return null;
    return processedOptions.find(opt => opt?.[valueField] === rowVal) || null;
  }, [params?.row, item?.sColumnID, processedOptions, valueField]);

  const [internalValue, setInternalValue] = useState(() => selectedValue || null);

  useEffect(() => {
    if (!open) {
      setInternalValue(selectedValue || null);
    }
  }, [selectedValue, open]);

  const filterOptions = useCallback((options, state) => {
    const search = (state.inputValue || "").toLowerCase();
    return options.filter(opt =>
      (opt._display || '').toLowerCase().includes(search)
    );
  }, []);

  const handleAccountChange = (e, newValue) => {
    setInternalValue(newValue);

    if (!newValue) {
      handleRowUpdate({
        fieldName: item?.sColumnID,
        value: null,
        rowId: params?.row?.id,
      });
      return;
    }

    handleRowUpdate({
      fieldName: item?.sColumnID,
      value: newValue?.[valueField],
      rowId: params?.row?.id,
    });
  };

  // Use backend field name from _fieldIds mapping if available, otherwise fall back to sName
  const fieldId = params?.row?._fieldIds?.[item?.sColumnID] || item?.inputType?.component?.sName;

  return (
    <Autocomplete
      options={processedOptions}
      name={item?.inputType?.component?.sName}
      id={`${fieldId}-${params?.row?.id}`}
      value={internalValue}
      onChange={handleAccountChange}
      getOptionLabel={(option) => option?._display || ''}
      isOptionEqualToValue={(option, value) =>
        option?.[valueField] === value?.[valueField]
      }
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setInternalValue(selectedValue || null);
      }}
      renderInput={(inputParams) => {
        const { inputProps, ...restParams } = inputParams;
        const { onKeyDown: originalOnKeyDown, value: inputValueFromParams, ...restInputProps } = inputProps || {};

        const wrappedInputProps = {
          ...restInputProps,
          value: typeof inputValueFromParams === 'string'
            ? inputValueFromParams.replace(/<[^>]+>/g, "")
            : inputValueFromParams,
          onKeyDown: (e) => {
            if (typeof originalOnKeyDown === 'function') {
              originalOnKeyDown(e);
            }
            const key = e.key || '';
            const code = e.code || '';
            if (key === ' ' || key === 'Spacebar' || code === 'Space') {
              e.stopPropagation();
            }
          }
        };

        return (
          <TextField
            {...restParams}
            inputProps={wrappedInputProps}
            placeholder="Select Account"
            size="medium"
          />
        );
      }}
      clearIcon={<CloseIcon fontSize="small" />}
      filterOptions={filterOptions}
      {...item?.inputType?.component?.sProps}
    />
  );
};

export default ColumnAccount;
